import { POINTER_CLASSES, Directions } from "../config/config";
// import { logDetailed } from "../helpers/utils";
import { boxSize } from "./canvasUtils";


// determines if a given point is close to a given line segment
// line segment is defined by the points p1, p2
export function isInBoundsLine(x, y, p1x, p1y, p2x, p2y, range) {
  if (!range) range = boxSize;
  if (x < Math.min(p1x, p2x) - range) return false;
  if (y < Math.min(p1y, p2y) - range) return false;
  if (x > Math.max(p1x, p2x) + range) return false;
  if (y > Math.max(p1y, p2y) + range) return false;

  let dy = p2y - p1y;
  let dx = p2x - p1x;

  let numerator = Math.abs(dy * x - dx * y
    + p2x * p1y - p2y * p1x);
  let denominator = Math.sqrt(dy * dy + dx * dx);
  let distance = numerator / denominator;
  return distance <= range;
}

export function displayAngle(angleRad) {
  return (angleRad * 180 / Math.PI).toFixed(1) + "°";
}

export function normalizeAngle(angle) {
  while (angle < -Math.PI) angle += 2 * Math.PI;
  while (angle > Math.PI) angle -= 2 * Math.PI;
  return angle;
}

export function segmentLengthPoints(p1, p2) {
  let dx = p2.x - p1.x;
  let dy = p2.y - p1.y;
  return segmentLengthDisp(dx, dy);
}

export function segmentLengthDisp(dx, dy) {
  return Math.sqrt(dx * dx + dy * dy);
}

export const sidesFlag = {
  top: 1,
  left: 2,
  right: 4,
  bottom: 8
};

export const sidesAndCorners = [
  sidesFlag.top,
  sidesFlag.left,
  sidesFlag.left | sidesFlag.top,
  sidesFlag.right,
  sidesFlag.right | sidesFlag.top,
  sidesFlag.bottom,
  sidesFlag.bottom | sidesFlag.left,
  sidesFlag.bottom | sidesFlag.right
];

export const sideCornerDetails = {
  [sidesFlag.top]: {
    theta: -Math.PI / 2,
    side: sidesFlag.top,
    hoverClass: POINTER_CLASSES.nsResize
  },
  [sidesFlag.left]: {
    theta: Math.PI,
    side: sidesFlag.left,
    hoverClass: POINTER_CLASSES.ewResize
  },
  [sidesFlag.top | sidesFlag.left]: {
    theta: -3 * Math.PI / 4,
    side: sidesFlag.top | sidesFlag.left,
    hoverClass: POINTER_CLASSES.nwseResize
  },
  [sidesFlag.right]: {
    theta: 0,
    side: sidesFlag.right,
    hoverClass: POINTER_CLASSES.ewResize
  },
  [sidesFlag.top | sidesFlag.right]: {
    theta: -Math.PI / 4,
    side: sidesFlag.top | sidesFlag.right,
    hoverClass: POINTER_CLASSES.neswResize
  },
  [sidesFlag.bottom]: {
    theta: Math.PI / 2,
    side: sidesFlag.bottom,
    hoverClass: POINTER_CLASSES.nsResize
  },
  [sidesFlag.bottom | sidesFlag.left]: {
    theta: 3 * Math.PI / 4,
    side: sidesFlag.bottom | sidesFlag.left,
    hoverClass: POINTER_CLASSES.neswResize
  },
  [sidesFlag.bottom | sidesFlag.right]: {
    theta: Math.PI / 4,
    side: sidesFlag.bottom | sidesFlag.right,
    hoverClass: POINTER_CLASSES.nwseResize
  }
};

export function isInBoundsBox(x, y, boxX, boxY, zoomLevel = 1.0, size = boxSize) {
  return Math.abs(x - boxX) < Math.max(size/zoomLevel, size) && Math.abs(y - boxY) < Math.max(size/zoomLevel, size);
}

export function angleOffset(length, theta) {
  return { x: length * Math.cos(theta), y: length * Math.sin(theta) };
}

export function findIntersectionLastSegment(points) {
  let p1 = points[points.length - 1];
  let p2 = points[points.length - 2];
  return findIntersection(points, p1, p2, 3, 0);
}

export function findIntersectionClosingSegment(points) {
  let p1 = points[points.length - 1];
  let p2 = points[0];
  return findIntersection(points, p1, p2, 2, 1);
}

// find an intersection in a point chain, starting from the end
export function findIntersection(points, p1, p2, fromEnd, fromStart) {
  // note: skipping the check of {l-1,l-2} against {l-2,l-3} 
  // because that has a trivial intersection at l-2
  for (let i = points.length - fromEnd; i > fromStart; i--) {
    let p3 = points[i];
    let p4 = points[i - 1];

    let intersect = segmentIntersection(p1, p2, p3, p4);
    if (intersect) return { index: i, intersect };
  }
  return { index: -1, intersect: null };
}

// check if the two line segments defined by {p1,p2} and {p3,p4} have an intersection
// see: http://www.cs.swan.ac.uk/~cssimon/line_intersection.html
export function segmentIntersection(p1, p2, p3, p4) {
  let dx1 = p2.x - p1.x;
  let dy1 = p2.y - p1.y;
  let dx2 = p4.x - p3.x;
  let dy2 = p4.y - p3.y;

  let determinant = dx1 * dy2 - dx2 * dy1;
  if (determinant == 0) return null;

  let cdx = p1.x - p3.x;
  let cdy = p1.y - p3.y;

  let s = (dx1 * cdy - dy1 * cdx) / determinant;
  let t = (dx2 * cdy - dy2 * cdx) / determinant;

  let intersection = null;
  let intersectionFound = s >= 0 && s <= 1 && t >= 0 && t <= 1;
  if (intersectionFound) intersection = {
    x: p1.x + t * dx1,
    y: p1.y + t * dy1
  };

  return intersection;
}

// calculate the area of a polygon defined by a list of points
export function polygonArea(points) {
  let area = 0;
  let j = points.length - 1;
  for (let i = 0; i < points.length; i++) {
    let pj = points[j];
    let pi = points[i];
    area += (pi.x * pj.y) - (pi.y * pj.x);
    j = i;
  }
  return Math.abs(area / 2);
}

export function getArcPosition(centerX, centerY, radiusX, radiusY, theta) {
  return {
    x: centerX + radiusX * Math.cos(theta),
    y: centerY + radiusY * Math.sin(theta)
  };
}

export function defaultSize() {
  return {
    w: 0,
    h: 0
  };
}

export function defaultOffset(){
  return {
    x: 0,
    y: 0
  };
}

export function sizesEqual(a, b){
   if (a === b || !a || !b) return true;
   return (a.width || a.w || 0) == (b.width || b.w || 0)
    &&  (a.height || a.h || 0) == (b.height || b.h || 0);
}

export function setSize(local, external) {
  if (!local || !external) return;

  local.w = external.w || external.width || external.pixelWidth || 0;
  local.h = external.h || external.height || external.pixelHeight || 0;
}

export function resizeCanvas(canvas, fit, zoom, floor) {
  if (!canvas || !fit || !zoom || !floor) return;

  let fitW = fit.w * zoom, fitH = fit.h * zoom;
  canvas.w = fitW < floor.w ? floor.w : fitW;
  canvas.h = fitH < floor.h ? floor.h : fitH;
}

export function defaultTransform() {
  return {
    // location on the image that should be centered, as a fraction of the image size
    centerX: 0.5,
    centerY: 0.5,

    // zoom level of the main image
    zoom: 1,
    defaultZoom: null,

    // zoom bounds
    fitZoom: 1,
    maxZoom: 3,
    minZoom: 0.05,

    offsetX: 0,
    offsetY: 0,

    canvasSize: defaultSize(),
    flippedSize: defaultSize(),
    imgSize: defaultSize(),
    flippedImgSize: defaultSize()
  };
}

export function setTransform(local, external){
  local.centerX = external.centerX || 0;
  local.centerY = external.centerY || 0;
  local.zoom = external.zoom || 0;
  local.fitZoom = external.fitZoom || 0;
  local.maxZoom = external.maxZoom || 0;
  local.offsetX = external.offsetX || 0;
  local.offsetY = external.offsetY || 0;
  setSize(local.canvasSize, external.canvasSize);
  setSize(local.imgSize, external.imgSize);
}

export function getZoom(parentSize, childSize) {
  return Math.min(parentSize.w / childSize.w, parentSize.h / childSize.h);
}

export function setZoom(transform, zoom, useFitZoom = false) {
  transform.zoom = bound(useFitZoom ? transform.fitZoom : zoom, transform.minZoom, transform.maxZoom);
  setCenter(transform, transform.centerX, transform.centerY);
}

export function setZoomBounds(transform, minZoom, maxZoom) {
  transform.minZoom = minZoom;
  transform.maxZoom = maxZoom;
}

export function setFitZoom(transform, zoom, useFitZoom){
  transform.fitZoom = zoom;
  transform.maxZoom = Math.ceil(zoom * 2 + 2);
  if (useFitZoom && !transform.defaultZoom) setZoom(transform, transform.fitZoom);
  else if (transform.defaultZoom) {
    if (transform.defaultZoom <= transform.maxZoom && transform.defaultZoom >= transform.minZoom) {
      setZoom(transform, transform.defaultZoom);
    }
    else {
      if (transform.defaultZoom > transform.maxZoom)
        setZoom(transform, transform.maxZoom);
      else if (transform.defaultZoom < transform.minZoom)
        setZoom(transform, transform.minZoom);
    }
  } 
}

export function setCenter(transform, centerX, centerY) {
  transform.centerX = getBoundedCenter(centerX, transform.canvasSize.w, transform.imgSize.w * transform.zoom);
  transform.centerY = getBoundedCenter(centerY, transform.canvasSize.h, transform.imgSize.h * transform.zoom);
}

function getBoundedCenter(center, canvas, img) {
  if (canvas >= img) return 0.5;
  let min = canvas / (2 * img);
  return bound(center, min, 1 - min);
}

export function bound(value, min, max) {
  return Math.max(min, Math.min(value, max));
}

// given an image, transformation settings, and a box to fit the image into
// calculate and return the offsets such that the transform.center will stick to the
// center of the image and fit
export function setOffsets(transform) {
  const zoomW = transform.imgSize.w * transform.zoom;
  const zoomH = transform.imgSize.h * transform.zoom;
  const centerLocX = zoomW * transform.centerX;
  const centerLocY = zoomH * transform.centerY;
  let offsetX = transform.canvasSize.w / 2 - centerLocX;
  let offsetY = transform.canvasSize.h / 2 - centerLocY;

  transform.offsetX = offsetX;
  transform.offsetY = offsetY;
}

export function setTransformSizes(transform, canvasSize, imgSize) {
  setSize(transform.canvasSize, canvasSize);
  setSize(transform.imgSize, imgSize);
  setSize(transform.flippedSize, {w: canvasSize.h, h: canvasSize.w});
  setSize(transform.flippedImgSize, {w: imgSize.h, h: imgSize.w});
}

export function getFittedSize(parentSize, childSize, margin = 0) {
  let marginLeft = 0, marginTop = 0, marginRight = 0, marginBottom = 0;
  if (typeof margin === "object" && margin !== null) {
    marginLeft = margin.left || 0;
    marginTop = margin.top || 0;
    marginRight = margin.right || 0;
    marginBottom = margin.bottom || 0;
  } else {
    marginLeft = marginTop = marginRight = marginBottom = margin;
  }

  let parentWithMargins = { w: parentSize.w - marginLeft - marginRight, h: parentSize.h - marginTop - marginBottom };
  const zoom = getZoom(parentWithMargins, childSize);
  return {
    zoom,
    w: zoom * childSize.w,
    h: zoom * childSize.h
  };
}

export function setFittedSize(parentSize, childSize, fitSize, margin = 0, hasBounds = true) {
  const fit = getFittedSize(parentSize, childSize, margin);
  if (hasBounds) setSize(fitSize, fit);
  return fit;
}

export function setCirrusRefereceFit(parentSize, sliceBounds, fitSize) {
  let aspectFit = getSquareFit(parentSize);
  if (sliceBounds) {
    let dx = sliceBounds.maxX - sliceBounds.minX;
    let dy = sliceBounds.maxY - sliceBounds.minY;
    if (dx && dy) aspectFit.w /= dx;
    if (dy && dy) aspectFit.h /= dy;
  }
  setFittedSize(parentSize, aspectFit, fitSize, 40);
}

export function setSpectralisReferenceFit(parentSize, sliceBounds, fitSize) {
  let aspectFit = getSquareFit(parentSize);
  setFittedSize(parentSize, aspectFit, fitSize, 15);
}

function getSquareFit(size) {
  let w = Math.min(size.w, size.h);
  return { h: w, w: w };
}

// modify the size of the large view and rgb view so that they fit within the main view
export function setRGBSizes(mainViewSize, rgbExpanded, largeViewSize, largeViewFit, rgbSize, isOptos) {
  // logDetailed("geometry", "setRGBSizes");
  let lvFactor = 0.5;
  if (rgbExpanded || isOptos)
    lvFactor = 0.67;
  
  let rgbFactor = (1 - lvFactor);
  if (!isOptos)
    rgbFactor /= 2;
  
  let lvs = Object.assign({}, mainViewSize);
  let rgbs = Object.assign({}, mainViewSize);

  lvs.w *= lvFactor;

  rgbs.w *= rgbFactor;
  if (isOptos)
    rgbs.h /= 3;
  else
    rgbs.h /= 2;

    
  setSize(largeViewSize, lvs);
  setSize(largeViewFit, lvs);
  setSize(rgbSize, rgbs);

  // logDetailed("geometry", "setRGBSizes", "lvs", lvs, "rgbs", rgbs);
}

export function withinViewBounds(x, y, viewSize = { w: 0, h: 0 }, offset = { x: 0, y: 0 }) {
  // returns true if the position is within a box offset from the center of a view
  // assumes that the fit view is centered within the view
  return x >= offset.x && y >= offset.y 
    && x <= (viewSize.w - offset.x) && y <= (viewSize.h - offset.y);
}

export function outOfBoundsDirection(x, y, viewSize = { w: 0, h: 0 }, offset = { x: 0, y: 0 }) {
  if((x < offset.x) || (x > (viewSize.w - offset.x))){
    return Directions.Horizontal;
  }
  else if((y < offset.y) ||(y > (viewSize.h - offset.y))){
    return Directions.Vertical;
  }else{
    return Directions.Unknown;
  }
}

export function viewOffset(viewSize, fitSize){
  // calculate the position of the fitted large view canvas relative to its container
  // for this browser, the main view is fitted in the center
  const x = (viewSize.w - fitSize.w) / 2;
  const y = (viewSize.h - fitSize.h) / 2;
  return { x, y };
}

export function calculateRealLocation(x, y, offset, transform, useFlipped = false) {
  return oldCalculateRealLocation(x, y, offset, transform, useFlipped == true ? transform.flippedSize : transform.canvasSize, useFlipped == true ? transform.flippedImgSize : transform.imgSize);
}

export function oldCalculateRealLocation(x, y, offset, transform, canvasSize, imgSize) {
  if (!transform || !offset || !x || !y)
  return null;
  // position of the center of the view, relative to the corner of the view
  const viewCenterOffsetX = (0.5 * canvasSize.w) / transform.zoom;
  const viewCenterOffsetY = (0.5 * canvasSize.h) / transform.zoom;
  // position of the center of the view, relative to the corner of the image
  const centerX = transform.centerX * imgSize.w;
  const centerY = transform.centerY * imgSize.h;
  // position of the corner of the view, relative to the corner of the image
  const cornerX = centerX - viewCenterOffsetX;
  const cornerY = centerY - viewCenterOffsetY;
  // position of the mouse, relative to the corner of the view
  const mouseX = (x - offset.x) / transform.zoom;
  const mouseY = (y - offset.y) / transform.zoom;

  let realX = cornerX + mouseX;
  let realY = cornerY + mouseY;

  return { x: realX, y: realY };
}

export function getCollectionBounds(collection){
  if (!collection || !collection.length) return defaultCollectionBounds();
  let minX = collection[0].startPoint.x;
  let maxX = minX;
  let minY = collection[0].startPoint.y;
  let maxY = minY;

  for (let i = 0; i < collection.length; i++){
    let {startPoint, endPoint} = collection[i];
    minX = Math.min(minX, startPoint.x, endPoint.x);
    maxX = Math.max(maxX, startPoint.x, endPoint.x);
    minY = Math.min(minY, startPoint.y, endPoint.y);
    maxY = Math.max(maxY, startPoint.y, endPoint.y);
  }

  return { minX, maxX, minY, maxY };
}

export function defaultCollectionBounds(){
  return {
    minX: 0, maxX: 1,
    minY: 0, maxY: 1
  };
}

export function getViewSize(id){
  var el = document.getElementById(id);
  return elementSize(el);
}

export function getViewSizeByClassName(className)  {
  var el = document.getElementsByClassName(className)[0];
  return elementSize(el);
}

function elementSize(el){
  return {
    w: el && el.clientWidth || 0,
    h: el && el.clientHeight || 0
  };
}

export function minCenterY(canvasSize, imgSize, transform){
  return canvasSize.h / (2 * imgSize.h * transform.zoom);
}

export function scrollCenterYConversion(canvasSize, imgSize, transform){
  return (1 - canvasSize.h / (imgSize.h * transform.zoom));
}

export function scrollFromCenterY(centerY, canvasSize, imgSize, transform){
  let minCenter = minCenterY(canvasSize, imgSize, transform);
  let conversion = scrollCenterYConversion(canvasSize, imgSize, transform);
  return (centerY - minCenter) / conversion;
}

export function centerYFromScroll(scroll, canvasSize, imgSize, transform){
  let minCenter = minCenterY(canvasSize, imgSize, transform);
  let conversion = scrollCenterYConversion(canvasSize, imgSize, transform);
  return minCenter + scroll * conversion;
}